import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  Image as ImageIcon,
  Inventory2 as Inventory2Icon,
  Work as WorkIcon,
  Star as StarIcon,
  RocketLaunch as RocketLaunchIcon,
  CalendarMonth as CalendarMonthIcon,
  SupportAgent as SupportAgentIcon,
  Insights as InsightsIcon,
  Description as DescriptionIcon,
  Paid as PaidIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  ToggleOn as ToggleOnIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  PlatformSubscriptionsService,
  SubscriptionBenefit,
  SubscriptionBenefitKey,
  SubscriptionPlan,
  SubscriptionTargetType,
} from '../../services/platformSubscriptionsService';

const BENEFIT_CATALOG: Array<{
  key: SubscriptionBenefitKey;
  label: string;
  kind: 'number' | 'boolean';
  defaultValue: number | boolean;
  icon: React.ReactElement;
}> = [
  { key: 'max_product_images', label: 'Max product images', kind: 'number', defaultValue: 10, icon: <ImageIcon fontSize="small" /> },
  { key: 'max_products', label: 'Max products (total)', kind: 'number', defaultValue: 50, icon: <Inventory2Icon fontSize="small" /> },
  { key: 'max_jobs', label: 'Max jobs (total)', kind: 'number', defaultValue: 25, icon: <WorkIcon fontSize="small" /> },
  { key: 'featured_products', label: 'Featured products', kind: 'boolean', defaultValue: true, icon: <StarIcon fontSize="small" /> },
  { key: 'featured_jobs', label: 'Featured jobs', kind: 'boolean', defaultValue: true, icon: <StarIcon fontSize="small" /> },
  { key: 'boost_credits_monthly', label: 'Boost credits / month', kind: 'number', defaultValue: 5, icon: <RocketLaunchIcon fontSize="small" /> },
  { key: 'visibility_days', label: 'Extra visibility days', kind: 'number', defaultValue: 14, icon: <CalendarMonthIcon fontSize="small" /> },
  { key: 'priority_support', label: 'Priority support', kind: 'boolean', defaultValue: true, icon: <SupportAgentIcon fontSize="small" /> },
  { key: 'advanced_analytics', label: 'Advanced analytics', kind: 'boolean', defaultValue: true, icon: <InsightsIcon fontSize="small" /> },
];

const benefitLabel = (b: SubscriptionBenefit): string => {
  const meta = BENEFIT_CATALOG.find((x) => x.key === b.key);
  const base = meta?.label || b.key;
  if (typeof b.value === 'boolean') return b.value ? base : `${base}: off`;
  return `${base}: ${b.value}`;
};

const benefitIcon = (b: SubscriptionBenefit): React.ReactElement | undefined => {
  return BENEFIT_CATALOG.find((x) => x.key === b.key)?.icon;
};

const normalizeBenefits = (raw: any): SubscriptionBenefit[] => {
  if (!Array.isArray(raw)) return [];
  const out: SubscriptionBenefit[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const key = (it as any).key as SubscriptionBenefitKey;
    const value = (it as any).value;
    if (!key) continue;
    const meta = BENEFIT_CATALOG.find((x) => x.key === key);
    if (!meta) continue;
    if (meta.kind === 'boolean') {
      out.push({ key, value: value === false ? false : true });
      continue;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) continue;
    out.push({ key, value: Math.max(0, n) });
  }
  const seen = new Set<string>();
  return out.filter((b) => {
    if (seen.has(b.key)) return false;
    seen.add(b.key);
    return true;
  });
};

const isBenefitBoolean = (key: SubscriptionBenefitKey): boolean => {
  return BENEFIT_CATALOG.find((x) => x.key === key)?.kind === 'boolean';
};

const emptyPlan = (): SubscriptionPlan => ({
  id: '',
  name: '',
  description: '',
  currency: 'EUR',
  price: 0,
  interval_unit: 'month',
  interval_count: 1,
  benefits: [],
  is_active: true,
  created_at: null,
  updated_at: null,
});

type ViewTab = 'plans' | 'assign';

const PlatformSubscriptionsTab: React.FC = () => {
  const [tab, setTab] = React.useState<ViewTab>('plans');

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [plans, setPlans] = React.useState<SubscriptionPlan[]>([]);
  const [updatedAt, setUpdatedAt] = React.useState<string | null>(null);

  const [planDialogOpen, setPlanDialogOpen] = React.useState(false);
  const [editPlan, setEditPlan] = React.useState<SubscriptionPlan>(emptyPlan());

  const [newBenefitKey, setNewBenefitKey] = React.useState<SubscriptionBenefitKey>('max_product_images');
  const [newBenefitValue, setNewBenefitValue] = React.useState<number>(10);
  const [newBenefitBool, setNewBenefitBool] = React.useState<boolean>(true);

  const [assigning, setAssigning] = React.useState(false);
  const [targetType, setTargetType] = React.useState<SubscriptionTargetType>('user');
  const [targetId, setTargetId] = React.useState('');
  const [planId, setPlanId] = React.useState('');
  const [startAt, setStartAt] = React.useState<string>('');
  const [endAt, setEndAt] = React.useState<string>('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const rows = await PlatformSubscriptionsService.listPlans();
      setPlans(rows);
      setUpdatedAt(new Date().toISOString());
      if (!planId && rows.length > 0) setPlanId(rows[0].id);
    } catch (e: any) {
      console.error('Load plans error:', e);
      toast.error(e?.message || 'Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditPlan(emptyPlan());
    setNewBenefitKey('max_product_images');
    setNewBenefitValue(10);
    setNewBenefitBool(true);
    setPlanDialogOpen(true);
  };

  const openEdit = (p: SubscriptionPlan) => {
    setEditPlan({ ...p, benefits: normalizeBenefits((p as any).benefits) });
    setNewBenefitKey('max_product_images');
    setNewBenefitValue(10);
    setNewBenefitBool(true);
    setPlanDialogOpen(true);
  };

  const addBenefit = () => {
    const meta = BENEFIT_CATALOG.find((x) => x.key === newBenefitKey);
    if (!meta) return;

    const next: SubscriptionBenefit =
      meta.kind === 'boolean'
        ? { key: meta.key, value: !!newBenefitBool }
        : { key: meta.key, value: Math.max(0, Number(newBenefitValue || 0)) };

    setEditPlan((p) => {
      const existing = Array.isArray(p.benefits) ? p.benefits : [];
      const filtered = existing.filter((b) => b.key !== next.key);
      return { ...p, benefits: [...filtered, next] };
    });
  };

  const removeBenefit = (key: SubscriptionBenefitKey) => {
    setEditPlan((p) => ({
      ...p,
      benefits: (Array.isArray(p.benefits) ? p.benefits : []).filter((b) => b.key !== key),
    }));
  };

  const updateBenefitValue = (key: SubscriptionBenefitKey, value: number | boolean) => {
    setEditPlan((p) => {
      const existing = Array.isArray(p.benefits) ? p.benefits : [];
      const next = existing.map((b) => (b.key === key ? { ...b, value } : b));
      return { ...p, benefits: next };
    });
  };

  const savePlan = async () => {
    const name = String(editPlan.name || '').trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const normalized: SubscriptionPlan = {
        ...editPlan,
        name,
        description: String(editPlan.description || '').trim(),
        currency: String(editPlan.currency || 'EUR').toUpperCase(),
        price: Number(editPlan.price || 0),
        interval_count: Math.max(1, Number(editPlan.interval_count || 1)),
        benefits: normalizeBenefits(editPlan.benefits),
      };

      await PlatformSubscriptionsService.upsertPlan(normalized);
      toast.success('Plan saved');
      setPlanDialogOpen(false);
      await load();
    } catch (e: any) {
      console.error('Save plan error:', e);
      toast.error(e?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const assign = async () => {
    const tId = targetId.trim();
    if (!tId) {
      toast.error('Target id is required');
      return;
    }
    if (!planId) {
      toast.error('Select a plan');
      return;
    }

    setAssigning(true);
    try {
      await PlatformSubscriptionsService.assignSubscription({
        target_type: targetType,
        target_id: tId,
        plan_id: planId,
        start_at: startAt || null,
        end_at: endAt || null,
      });
      toast.success('Subscription assigned');
      setTargetId('');
      setStartAt('');
      setEndAt('');
    } catch (e: any) {
      console.error('Assign error:', e);
      toast.error(e?.message || 'Failed to assign subscription');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Platform Subscriptions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create pricing models and assign subscriptions to users or companies.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip icon={<CreditCardIcon />} label={loading ? 'Loading…' : `${plans.length} plans`} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Plan
          </Button>
          <Button
            onClick={() => load()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'grey.50' },
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Card sx={{ boxShadow: '0 14px 40px rgba(15, 23, 42, 0.08)' }}>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab value="plans" label="Plans" />
            <Tab value="assign" label="Assign" />
          </Tabs>

          {tab === 'plans' && (
            <Stack spacing={1.5}>
              {plans.length === 0 && (
                <Box sx={{ p: 2.5 }}>
                  <Typography fontWeight={900}>No plans yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Create your first subscription plan.
                  </Typography>
                </Box>
              )}

              {plans.map((p) => (
                <Card
                  key={p.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={950} sx={{ lineHeight: 1.1 }}>
                        {p.name}
                      </Typography>
                      {p.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {p.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1.25, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={`${Number(p.price || 0).toFixed(2)} ${(p.currency || 'EUR').toUpperCase()}`}
                          sx={{ fontWeight: 900 }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`every ${p.interval_count} ${p.interval_unit}${p.interval_count > 1 ? 's' : ''}`}
                          sx={{ fontWeight: 900 }}
                        />
                        <Chip
                          size="small"
                          color={p.is_active ? 'success' : 'default'}
                          icon={p.is_active ? <CheckCircleIcon /> : undefined}
                          label={p.is_active ? 'Active' : 'Inactive'}
                          sx={{ fontWeight: 900 }}
                        />
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Button
                        onClick={() => openEdit(p)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          backgroundColor: 'white',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { backgroundColor: 'grey.50' },
                        }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </CardContent>

                  {Array.isArray(p.benefits) && p.benefits.length > 0 && (
                    <>
                      <Divider />
                      <CardContent sx={{ pt: 1.75 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={900}>
                          Benefits
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                          {normalizeBenefits(p.benefits).map((b) => (
                            <Chip
                              key={`${p.id}-${b.key}`}
                              label={benefitLabel(b)}
                              variant="outlined"
                              sx={{ borderRadius: 999, fontWeight: 800 }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </>
                  )}
                </Card>
              ))}
            </Stack>
          )}

          {tab === 'assign' && (
            <Box sx={{ maxWidth: 760 }}>
              <Typography fontWeight={950}>Assign a subscription</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Pick a plan and assign it to a user or company by id.
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="sub-target-type">Target type</InputLabel>
                    <Select
                      labelId="sub-target-type"
                      label="Target type"
                      value={targetType}
                      onChange={(e) => setTargetType(e.target.value as SubscriptionTargetType)}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="sub-plan">Plan</InputLabel>
                    <Select labelId="sub-plan" label="Plan" value={planId} onChange={(e) => setPlanId(String(e.target.value))}>
                      {plans.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <TextField
                  fullWidth
                  label={targetType === 'company' ? 'Company ID (uuid)' : 'User ID (uuid)'}
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  placeholder="e.g. 7b8c8a6d-..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Start at (ISO / optional)"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                    placeholder="2026-02-01T00:00:00Z"
                  />
                  <TextField
                    fullWidth
                    label="End at (ISO / optional)"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                    placeholder="2026-03-01T00:00:00Z"
                  />
                </Stack>

                <Button variant="contained" onClick={assign} disabled={assigning} startIcon={<SaveIcon />}>
                  {assigning ? 'Assigning…' : 'Assign subscription'}
                </Button>
              </Stack>
            </Box>
          )}

          {updatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2.5 }}>
              Updated: {updatedAt}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontWeight: 950,
            pb: 1.5,
            background:
              'linear-gradient(135deg, rgba(17, 24, 39, 0.06), rgba(249, 115, 22, 0.10), rgba(17, 24, 39, 0.03))',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.18), rgba(17, 24, 39, 0.06))',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CreditCardIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                  {editPlan.id ? 'Edit plan' : 'New plan'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Pricing, interval and benefits
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={() => setPlanDialogOpen(false)}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'grey.50' },
              }}
            >
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.25 }}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography fontWeight={950} sx={{ mb: 1.5 }}>
                  Plan details
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    value={editPlan.name}
                    onChange={(e) => setEditPlan((p) => ({ ...p, name: e.target.value }))}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CreditCardIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Description"
                    value={editPlan.description || ''}
                    onChange={(e) => setEditPlan((p) => ({ ...p, description: e.target.value }))}
                    fullWidth
                    multiline
                    minRows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography fontWeight={950} sx={{ mb: 1.5 }}>
                  Pricing & billing
                </Typography>

                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Price"
                      type="number"
                      value={editPlan.price}
                      onChange={(e) => setEditPlan((p) => ({ ...p, price: Number(e.target.value) }))}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PaidIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Currency"
                      value={editPlan.currency}
                      onChange={(e) => setEditPlan((p) => ({ ...p, currency: e.target.value }))}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencyExchangeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel id="interval-unit">Interval</InputLabel>
                      <Select
                        labelId="interval-unit"
                        label="Interval"
                        value={editPlan.interval_unit}
                        onChange={(e) => setEditPlan((p) => ({ ...p, interval_unit: e.target.value as any }))}
                        startAdornment={
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="day">Day</MenuItem>
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Every (count)"
                      type="number"
                      value={editPlan.interval_count}
                      onChange={(e) => setEditPlan((p) => ({ ...p, interval_count: Number(e.target.value) }))}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.25,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.02), rgba(249, 115, 22, 0.06))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <ToggleOnIcon fontSize="small" />
                      <Box>
                        <Typography fontWeight={950} sx={{ lineHeight: 1.1 }}>
                          Status
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {editPlan.is_active ? 'Active (visible in plan picker)' : 'Inactive (hidden from normal selection)'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 950, color: editPlan.is_active ? 'success.main' : 'text.secondary' }}>
                        {editPlan.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Switch
                        checked={!!editPlan.is_active}
                        onChange={(e) => setEditPlan((p) => ({ ...p, is_active: e.target.checked }))}
                        color="success"
                      />
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                  <Typography fontWeight={950}>Benefits (entitlements)</Typography>
                  <Chip
                    size="small"
                    label={`${normalizeBenefits(editPlan.benefits).length} selected`}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Choose what the subscription unlocks. Most benefits take a number; some are simple on/off.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'stretch' }}>
                  <FormControl fullWidth>
                    <InputLabel id="benefit-key">Benefit</InputLabel>
                    <Select
                      labelId="benefit-key"
                      label="Benefit"
                      value={newBenefitKey}
                      onChange={(e) => {
                        const nextKey = e.target.value as SubscriptionBenefitKey;
                        setNewBenefitKey(nextKey);
                        const meta = BENEFIT_CATALOG.find((x) => x.key === nextKey);
                        if (!meta) return;
                        if (meta.kind === 'boolean') {
                          setNewBenefitBool(Boolean(meta.defaultValue));
                        } else {
                          setNewBenefitValue(Number(meta.defaultValue));
                        }
                      }}
                      renderValue={(value) => {
                        const meta = BENEFIT_CATALOG.find((x) => x.key === value);
                        return (
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Box sx={{ display: 'grid', placeItems: 'center' }}>{meta?.icon}</Box>
                            <Typography sx={{ fontWeight: 900 }}>{meta?.label ?? String(value)}</Typography>
                          </Stack>
                        );
                      }}
                    >
                      {BENEFIT_CATALOG.map((b) => (
                        <MenuItem key={b.key} value={b.key}>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Box sx={{ display: 'grid', placeItems: 'center' }}>{b.icon}</Box>
                            <Typography sx={{ fontWeight: 800 }}>{b.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {(() => {
                    const meta = BENEFIT_CATALOG.find((x) => x.key === newBenefitKey);
                    if (meta?.kind === 'boolean') {
                      return (
                        <FormControl fullWidth>
                          <InputLabel id="benefit-bool">Enabled</InputLabel>
                          <Select
                            labelId="benefit-bool"
                            label="Enabled"
                            value={newBenefitBool ? '1' : '0'}
                            onChange={(e) => setNewBenefitBool(e.target.value === '1')}
                          >
                            <MenuItem value="1">On</MenuItem>
                            <MenuItem value="0">Off</MenuItem>
                          </Select>
                        </FormControl>
                      );
                    }
                    return (
                      <TextField
                        fullWidth
                        label="Value"
                        type="number"
                        value={newBenefitValue}
                        onChange={(e) => setNewBenefitValue(Number(e.target.value))}
                      />
                    );
                  })()}

                  <Button
                    variant="contained"
                    onClick={addBenefit}
                    startIcon={<AddIcon />}
                    sx={{
                      whiteSpace: 'nowrap',
                      borderRadius: 2.5,
                      fontWeight: 950,
                      px: 2.25,
                      boxShadow: '0 18px 44px rgba(249, 115, 22, 0.18)',
                    }}
                  >
                    Add benefit
                  </Button>
                </Stack>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {normalizeBenefits(editPlan.benefits).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No benefits selected.
                    </Typography>
                  )}
                </Box>

                {normalizeBenefits(editPlan.benefits).length > 0 && (
                  <Stack spacing={1.25} sx={{ mt: 1.75 }}>
                    {normalizeBenefits(editPlan.benefits).map((b) => {
                      const meta = BENEFIT_CATALOG.find((x) => x.key === b.key);
                      const bool = isBenefitBoolean(b.key);
                      return (
                        <Box
                          key={b.key}
                          sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            background:
                              'linear-gradient(135deg, rgba(17, 24, 39, 0.02), rgba(249, 115, 22, 0.04), rgba(17, 24, 39, 0.01))',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 260 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: 'grid',
                                placeItems: 'center',
                                border: '1px solid',
                                borderColor: 'divider',
                                background: 'white',
                              }}
                            >
                              {meta?.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={950} sx={{ lineHeight: 1.1 }}>
                                {meta?.label ?? b.key}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {bool ? 'Toggle benefit on/off' : 'Set limit / amount'}
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                            {bool ? (
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography sx={{ fontWeight: 950, color: b.value ? 'success.main' : 'text.secondary' }}>
                                  {b.value ? 'On' : 'Off'}
                                </Typography>
                                <Switch
                                  checked={!!b.value}
                                  onChange={(e) => updateBenefitValue(b.key, e.target.checked)}
                                  color="success"
                                />
                              </Stack>
                            ) : (
                              <TextField
                                label="Value"
                                type="number"
                                value={typeof b.value === 'number' ? b.value : 0}
                                onChange={(e) => updateBenefitValue(b.key, Math.max(0, Number(e.target.value || 0)))}
                                sx={{ width: 180 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      {meta?.icon}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}

                            <IconButton
                              onClick={() => removeBenefit(b.key)}
                              sx={{
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { backgroundColor: 'grey.50' },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            background:
              'linear-gradient(135deg, rgba(17, 24, 39, 0.02), rgba(249, 115, 22, 0.06), rgba(17, 24, 39, 0.01))',
          }}
        >
          <Button
            onClick={() => setPlanDialogOpen(false)}
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'grey.50' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={savePlan}
            disabled={saving}
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 950,
              px: 2.5,
              boxShadow: '0 20px 50px rgba(249, 115, 22, 0.22)',
            }}
          >
            {saving ? 'Saving…' : 'Save plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformSubscriptionsTab;
